import PropTypes from 'prop-types';
import { Typography, Tooltip } from 'antd';
import { IconArrowUp, IconArrowDown, IconMinus } from '../icons';
import IconTile from './IconTile';
import './primitives.css';

const { Text } = Typography;

/**
 * StatCard
 *
 * KPI tile for dashboards. An IconTile leads it — `docs/design-language.md` §2
 * puts one on "every navigational row, service entry and stat" — and the tile
 * carries the tone, which is why the old 3px accent bar on the left edge is
 * gone: two carriers of the same state on one card is decoration.
 *
 * The value is a figure, so it is set in the mono face (§2: times, scores,
 * counts, money, countdowns) and cannot change width as it counts.
 *
 * Tones are the five semantic names this component has always taken, plus the
 * seven functional hues, so a stat about tuition can be terracotta and one
 * about the question bank indigo without inventing a colour.
 *
 * `variant="cell"` drops the surface, border and radius and keeps everything
 * else, so several stats can share one instrument instead of floating as four
 * separate boxes — the dashboard's pulse strip
 * (`design/web/archetypes/dashboard.md` §3). A standalone stat keeps `card`.
 */
const TrendIcon = ({ direction }) => {
  if (direction === 'up') return <IconArrowUp />;
  if (direction === 'down') return <IconArrowDown />;
  return <IconMinus />;
};

TrendIcon.propTypes = { direction: PropTypes.oneOf(['up', 'down', 'flat']) };

const StatCard = ({
  label,
  value,
  unit,
  icon,
  tone = 'neutral',
  subject,
  variant = 'card',
  trend,
  trendLabel,
  helpText,
  onClick,
  className = '',
}) => {
  const interactive = typeof onClick === 'function';
  const trendDirection = trend > 0 ? 'up' : trend < 0 ? 'down' : 'flat';
  const trendClass =
    trendDirection === 'up'
      ? 'lms-stat-card__trend--up'
      : trendDirection === 'down'
        ? 'lms-stat-card__trend--down'
        : 'lms-stat-card__trend--flat';

  return (
    <div
      className={[
        'lms-stat-card',
        `lms-stat-card--${tone}`,
        variant === 'cell' ? 'lms-stat-card--cell' : '',
        interactive ? 'lms-stat-card--interactive' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      onKeyDown={(e) => {
        if (interactive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick(e);
        }
      }}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      {icon && (
        <IconTile
          icon={icon}
          tone={tone}
          subject={subject}
          size="md"
          className="lms-stat-card__icon"
        />
      )}

      <div className="lms-stat-card__content">
        <Text type="secondary" className="lms-stat-card__label">
          {label}
        </Text>

        <div className="lms-stat-card__value-row">
          <span className="lms-stat-card__value lms-figure">{value}</span>
          {unit && <span className="lms-stat-card__unit">{unit}</span>}
        </div>

        {(trend !== undefined || helpText || trendLabel) && (
          <div className="lms-stat-card__foot">
            {trend !== undefined && (
              <span className={`lms-stat-card__trend ${trendClass}`}>
                <TrendIcon direction={trendDirection} />
                {Math.abs(trend)}%
              </span>
            )}
            {helpText &&
              (typeof helpText === 'string' ? (
                <Tooltip title={helpText}>
                  <Text type="secondary" className="lms-stat-card__help">
                    {trendLabel}
                  </Text>
                </Tooltip>
              ) : (
                <Text type="secondary" className="lms-stat-card__help">
                  {trendLabel || helpText}
                </Text>
              ))}
            {!helpText && trendLabel && (
              <Text type="secondary" className="lms-stat-card__help">
                {trendLabel}
              </Text>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

StatCard.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  unit: PropTypes.node,
  icon: PropTypes.node,
  tone: IconTile.propTypes.tone,
  /** Course id, when the stat is about one subject — colours the tile with it. */
  subject: IconTile.propTypes.subject,
  /** `cell` gives up surface, border and radius to sit in a shared strip. */
  variant: PropTypes.oneOf(['card', 'cell']),
  trend: PropTypes.number,
  trendLabel: PropTypes.node,
  helpText: PropTypes.node,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default StatCard;
